import React, { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { boolean, number, object, string } from 'yup'
import { useTranslation } from 'react-i18next'
import { StyleSheet, ViewProps } from 'react-native'
import { Draft, ProductData, useProduct } from 'zens-store'
import { Text } from 'zens-storybook'
import { Form, NavigationConfirm, FormCheckbox, FormProps } from '@dms/component'
import { Href } from 'zens-router'

import ProductTypeSelect from './typeselect'
import OperatorSelect from './operatorselect'

const styles = StyleSheet.create({
  checkbox: {
    minHeight: 46,
  },
})

interface Props extends Omit<FormProps, 'draft' | 'config' | 'schema' | 'buttons'> {
  companyId: number
  productId?: number
  onSave?: (draft: Draft<ProductData>) => Promise<void>
  onDelete?: (draft: Draft<ProductData>) => Promise<void>
  cancelHref?: Href
  errors?: Record<keyof ProductData, string>
}
const ProductForm = (props: Props) => {
  const { companyId, productId, onSave, onDelete, cancelHref, ...otherProps } = props
  const { t } = useTranslation()

  const product = useProduct({ companyId, productId })

  const draft = useMemo(() => new Draft({ id: NaN, company_id: companyId, ...product.data?.data }), [product.data])

  const config = {
    groups: [
      {
        label: t('Product details'),
        fields: [
          {
            key: 'serial_number',
            label: t('Serial number'),
          },
          {
            key: 'product_type_id',
            label: t('Model'),
            FieldInputComponent: ProductTypeSelect,
          },
        ],
      },
      {
        label: t('Network details'),
        fields: [
          {
            key: 'operator',
            label: t('Provider'),
            FieldInputComponent: OperatorSelect,
          },
          {
            key: 'sim',
            label: t('SIM'),
          },
          {
            key: 'imei',
            label: t('IMEI'),
          },
          {
            key: 'band',
            label: t('Band'),
          },
        ],
      },
      {
        label: t('Beacon details'),
        fields: [
          {
            key: 'uuid_1',
            label: t('UUID'),
          },
          {
            key: 'major_1',
            label: t('Major'),
          },
          {
            key: 'minor_1',
            label: t('Minor'),
          },
          {
            key: 'beacon_2_is_enabled',
            FieldInputComponent: FormCheckbox,
            fieldProps: {
              label: t('Second beacon'),
              testID: 'BuidingForm.SecondBeacon',
            },
            style: styles.checkbox,
          },
          ...(draft.data.beacon_2_is_enabled
            ? [
                {
                  key: 'uuid_2',
                  label: t('UUID'),
                },
                {
                  key: 'major_2',
                  label: t('Major'),
                },
                {
                  key: 'minor_2',
                  label: t('Minor'),
                },
              ]
            : []),
        ],
      },
      {
        label: t('Sensor details'),
        fields: [
          {
            key: 'occupancy_sensor_model',
            label: t('Occupancy (model number)'),
            optional: true,
          },
        ],
      },
    ],
  }
  const schema = object({
    product_type_id: string().required(t('Model is required')),
    serial_number: string().required(t('Serial number is required')),
    operator: string().required(t('Provider is required')),
    sim: string().required(t('SIM is required')),
    imei: string().required(t('IMEI is required')),
    band: string().required(t('Band is required')),
    uuid_1: string().required(t('UUID is required')),
    major_1: number().typeError(t('Invalid major value')).required(t('Major is required')),
    minor_1: number().typeError(t('Invalid minor value')).required(t('Minor is required')),
    beacon_2_is_enabled: boolean(),
    ...(draft.data.beacon_2_is_enabled
      ? {
          uuid_2: string().required(t('UUID is required')),
          major_2: number().typeError(t('Invalid major value')).required(t('Major is required')),
          minor_2: number().typeError(t('Invalid minor value')).required(t('Minor is required')),
        }
      : {}),
  })
  return (
    <>
      <Form
        draft={draft}
        config={config}
        schema={schema}
        buttons={[
          [
            {
              title: t('Save'),
              onPress: onSave,
              validate: true,
            },
            {
              title: t('Cancel'),
              href: cancelHref,
              light: true,
            },
          ],
          ...(onDelete
            ? [
                {
                  title: t('Delete device'),
                  onPress: onDelete,
                  left: 'trash' as const,
                  danger: true,
                },
              ]
            : []),
        ]}
        {...otherProps}
      />
      <NavigationConfirm draft={draft} />
    </>
  )
}

interface SkeletonProps extends ViewProps {}

const Skeleton = (props: SkeletonProps) => {
  const { t } = useTranslation()
  const config = {
    groups: [
      {
        label: t('Product details'),
        fields: [
          {
            key: 'serial_number',
            label: t('Serial number'),
          },
          {
            key: 'product_type_id',
            label: t('Model'),
          },
        ],
      },
      {
        label: t('Network details'),
        fields: [
          {
            key: 'operator',
            label: t('Provider'),
          },
          {
            key: 'sim',
            label: t('SIM'),
          },
          {
            key: 'imei',
            label: t('IMEI'),
          },
          {
            key: 'band',
            label: t('Band'),
          },
        ],
      },
      {
        label: t('Beacon details'),
        fields: [
          {
            key: 'uuid_1',
            label: t('UUID'),
          },
          {
            key: 'major_1',
            label: t('Major'),
          },
          {
            key: 'minor_1',
            label: t('Minor'),
          },
          {
            key: 'beacon_2_is_enabled',
            FieldInputComponent: Text.Skeleton,
            style: styles.checkbox,
          },
        ],
      },
      {
        label: t('Sensor details'),
        fields: [
          {
            key: 'occupancy_sensor_model',
            label: t('Occupancy (model number)'),
          },
        ],
      },
    ],
  }

  return <Form.Skeleton config={config} numberOfbuttons={2} {...props} />
}
ProductForm.Skeleton = Skeleton

export default observer(ProductForm)
